import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import { UsernameBadges } from '@freelancer/components/username';
import {
  flUserCog,
  IconColor,
  IconSize,
  provideFlIcons,
} from '@freelancer/ui/icon';
import { AvatarSize } from '@freelancer/ui/user-avatar';

@Component({
  standalone: false,
  selector: 'fl-comment-ui-content',
  template: `
    <div
      class="CommentText mb-xxsmall"
      [class.IsDisabled]="isDisabled"
    >
      <ng-content></ng-content>
    </div>
  `,
  styleUrls: ['comment-ui.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CommentUiContentComponent {
  @Input() isDisabled = false;
}

@Component({
  standalone: false,
  selector: 'fl-comment-ui-custom-user-title',
  template: `
    <p
      class="text-body-16 font-medium mr-xxsmall"
      [class.italic]="disabled"
    >
      <ng-content></ng-content>
    </p>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CommentUiCustomUserTitleComponent {
  @Input() disabled = false;
}

@Component({
  standalone: false,
  selector: 'fl-comment-ui-options',
  template: ` <ng-content></ng-content> `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CommentUiOptionsComponent {}

@Component({
  standalone: false,
  selector: 'fl-comment-ui-edit-form',
  template: ` <ng-content></ng-content> `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CommentUiEditFormComponent {}

@Component({
  standalone: false,
  selector: 'fl-comment-ui-action',
  template: `
    <fl-button
      class="CommentAction"
      flTrackingLabel="flTrackingLabel"
      [disabled]="disabled"
      (click)="handleAction()"
    >
      @if (icon) {
        <fl-icon
          class="CommentAction-icon mr-xxxsmall"
          [color]="active ? IconColor.SECONDARY : IconColor.FOREGROUND"
          [title]="label"
          [name]="icon"
          [useIconFont]="true"
          [fillIconFont]="icon === 'ui-thumb-up'"
          [size]="IconSize.SMALL"
        ></fl-icon>
      }
      <p
        class="text-body-16 CommentAction-text font-medium"
        i18n="FeedItemComment"
      >
        <ng-content></ng-content>
      </p>
    </fl-button>
  `,
  styleUrls: ['comment-ui.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CommentUiActionComponent {
  IconSize = IconSize;
  IconColor = IconColor;

  @Input() icon?: 'ui-chat-square' | 'ui-heart' | 'ui-thumb-up';
  @Input() disabled = false;
  @Input() active = true;
  @Input() label?: string;
  @Input() flTrackingLabel: string;
  @Output() action = new EventEmitter<void>();

  handleAction(): void {
    this.action.emit();
  }
}

@Component({
  standalone: false,
  selector: 'fl-comment-ui-actions',
  template: `
    <div class="ActionsContainer">
      <ng-content select="fl-comment-ui-action"></ng-content>
    </div>
  `,
  styleUrls: ['comment-ui.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CommentUiActionsComponent {}

@Component({
  standalone: false,
  selector: 'fl-comment-ui',
  template: `
    <div class="CommentItem">
      <fl-user-avatar
        class="mr-xxsmall"
        [size]="AvatarSize.SMALL"
        [sizeDesktop]="AvatarSize.MID"
        [users]="[{ avatar: avatar, username: username }]"
      ></fl-user-avatar>
      <div class="CommentItem-content">
        <div
          class="CommentItem-header {{
            isCompact || isEditMode ? 'mb-xxsmall' : 'mb-xxxsmall'
          }}"
        >
          <div
            class="CommentItem-header-details"
            [class.IsStacked]="isCompact"
            [class.IsDisabled]="isDisabled"
          >
            @if (showHeaderIcon) {
              <fl-icon
                class="mr-xxxsmall"
                i18n-matTooltip="Comment header icon tooltip"
                matTooltip="Group Administrator"
                [name]="'flUserCog'"
                [size]="IconSize.SMALL"
              ></fl-icon>
            }
            @if (useCustomUserTitle) {
              <ng-content select="fl-comment-ui-custom-user-title"></ng-content>
            } @else {
              <fl-username
                class="mr-xxsmall"
                flTrackingLabel="CommentUsername"
                [badges]="badges"
                [displayName]="displayName"
                [link]="'/u/' + username"
                [username]="username"
                [class.mobile:hide]="hideDisplayNameMobile"
              ></fl-username>
            }
            @if (!isCompact) {
              <p
                class="text-body-16 font-bold mr-xxsmall"
                i18n="Text interpunct"
              >
                ·
              </p>
            }

            <fl-relative-time
              i18n-matTooltip="Comment created time"
              matTooltip="{{ timeCreated | date: 'EEEE, MMM d, y' }} at {{
                timeCreated | date: 'HH:mm a'
              }}"
              [matTooltipPosition]="'above'"
              [date]="timeCreated"
              [includeSeconds]="false"
              [strict]="true"
            ></fl-relative-time>
          </div>
          <div class="CommentItem-header-options">
            <ng-content select="fl-comment-ui-options"></ng-content>
          </div>
        </div>
        @if (!isEditMode) {
          <ng-content select="fl-comment-ui-content"></ng-content>
        } @else {
          <ng-content select="fl-comment-ui-edit-form"></ng-content>
        }
        @if (actions) {
          <ng-content select="fl-comment-ui-actions"></ng-content>
        }
      </div>
    </div>
  `,
  styleUrls: ['comment-ui.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [provideFlIcons({ flUserCog })],
})
export class CommentUiComponent {
  AvatarSize = AvatarSize;
  IconSize = IconSize;

  @Input() avatar?: string;
  @Input() displayName: string;
  // Hides the display name on mobile.
  @Input() hideDisplayNameMobile = false;
  /** When true, shows the group administrator icon in the comment header. */
  @Input() showHeaderIcon = false;
  @Input() isCompact = false;
  @Input() isEditMode = false;
  @Input() timeCreated: number | string | Date;
  @Input() username: string;
  @Input() useCustomUserTitle = false;
  @Input() badges: UsernameBadges;
  @Input() isDisabled = false;
  @Input() actions = true;
}
